import { ReactNode } from "react"

export interface DescriptiveToggleOption<T extends string | number> {
    value: T
    title: string
    description?: string
    icon?: ReactNode
    badge?: ReactNode
    dataTest?: string
}

interface DescriptiveToggleProps<T extends string | number> {
    options: DescriptiveToggleOption<T>[]
    value: T | null
    onChange: (value: T) => void
    columns?: 1 | 2 | 3 | 4
    mobileColumns?: 1 | 2
    /** Dense landing-widget tiles: icon and title on one line, no description and no check. */
    compact?: boolean
    /** "tint" tints the selected card; "plain" keeps it white on an already pale-blue container. */
    selectedTone?: "tint" | "plain"
    hideDescriptionOnMobile?: boolean
    className?: string
    ariaLabel: string
}

const COLUMNS_CLASS: Record<1 | 2 | 3 | 4, string> = {
    1: "md:grid-cols-1",
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
}

// border-2, not border: .loan-field-has-error repaints every descendant carrying the `border` class
const SelectionCheck = ({ isSelected }: { isSelected: boolean }) => (
    <span
        aria-hidden="true"
        className={`ml-auto flex size-6 shrink-0 self-start items-center justify-center rounded-full ${
            isSelected ? "bg-rm-blue" : "border-2 border-rm-gray-light bg-white"
        }`}
    >
        {isSelected && <i className="fa-regular fa-check text-[11px] text-white" />}
    </span>
)

export default function DescriptiveToggle<T extends string | number>({
    options,
    value,
    onChange,
    columns = 2,
    mobileColumns = 1,
    compact = false,
    selectedTone = "tint",
    hideDescriptionOnMobile = false,
    className = "",
    ariaLabel,
}: DescriptiveToggleProps<T>) {
    return (
        <div
            role="radiogroup"
            aria-label={ariaLabel}
            className={`grid ${
                compact
                    ? "grid-cols-2 gap-3"
                    : `${mobileColumns === 2 ? "grid-cols-2" : "grid-cols-1"} ${COLUMNS_CLASS[columns]} gap-4`
            } ${className}`}
        >
            {options.map((option) => {
                const isSelected = value === option.value
                // A badge pushes the title below it, except in a single mobile column where the card stays horizontal
                const hasIcon = option.icon != null
                const stacked = !compact && hasIcon && mobileColumns === 2
                const stackedFromMd = !compact && hasIcon && mobileColumns === 1

                return (
                    <button
                        key={String(option.value)}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        data-test={option.dataTest}
                        onClick={() => onChange(option.value)}
                        className={`flex h-full items-center gap-x-3 gap-y-2 border-2 text-left text-pale transition-all duration-300 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue ${
                            compact ? "rounded-xl p-3 md:p-4" : "rounded-2xl p-4 md:p-6"
                        } ${
                            stacked ? "flex-wrap items-start" : ""
                        } ${stackedFromMd ? "md:flex-wrap md:items-start" : ""} ${
                            isSelected
                                ? `border-rm-blue ${selectedTone === "tint" ? "bg-rm-blue-pale" : "bg-white"}`
                                : "border-rm-gray-light bg-white hover:border-rm-gray"
                        }`}
                    >
                        {option.icon != null && (
                            <span
                                className={
                                    compact
                                        ? "text-2xl leading-none"
                                        : `flex size-11 shrink-0 items-center justify-center rounded-xl text-[18px] leading-none ${
                                              isSelected ? "bg-white" : "bg-neutral"
                                          }`
                                }
                                aria-hidden="true"
                            >
                                {option.icon}
                            </span>
                        )}

                        <span
                            className={`flex flex-1 flex-col gap-1 ${
                                stacked ? "order-last w-full flex-none" : ""
                            } ${stackedFromMd ? "md:order-last md:w-full md:flex-none" : ""}`}
                        >
                            <span
                                className={`leading-snug ${
                                    compact ? "text-base font-semibold" : "text-base font-bold md:text-lg"
                                }`}
                            >
                                {option.title}
                            </span>
                            {option.badge}
                            {!compact && option.description && (
                                <span
                                    className={`text-sm font-normal leading-relaxed text-pale/80 ${
                                        hideDescriptionOnMobile ? "hidden md:inline" : ""
                                    }`}
                                >
                                    {option.description}
                                </span>
                            )}
                        </span>

                        {!compact && <SelectionCheck isSelected={isSelected} />}
                    </button>
                )
            })}
        </div>
    )
}
